import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResultAnswers } from 'store/player/playerAsync';
import { selectResult } from 'store/player/playerSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
import PageTypes from 'types/PageTypes';
// MUI
import { Box } from '@mui/material';
// Components
import AppLoader from 'components/AppLoader';
import QRCode from './QRCode';
import AnswerList from './AnswersList';
// i18next
import i18n from 'i18n'
// Hooks
import useCompactLayout from 'hooks/useCompactLayout';

let interval: any;
let questionInterval: any;

const LiveResultsPage:FC = () => {
  const { slug } = useParams<{ slug:string }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [progress, setProgress] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.matchMedia("(min-width: 640px)").matches);
  const isCompact = useCompactLayout();

  const mediaHandler = (e:any) => setIsLargeScreen(e.matches);

  useEffect(() => {
    window.matchMedia("(min-width: 640px)").addEventListener('change', mediaHandler);

    return () => {
      window.matchMedia("(min-width: 640px)").removeEventListener('change', mediaHandler);
    }
  }, []);

  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz | null = result?.quiz || null;
  const questionPages = quiz?.pages?.filter(page => page.type === PageTypes.Question);
  const sortedQuestions = questionPages?.map(page => quiz?.questions.find(q => page.refs.includes(q.ref))) || [];
  const isQrSlide = isCompact && !!questionPages && questionIndex === questionPages.length;
  const question = sortedQuestions[questionIndex];
  const results = result?.results || [];
  const questionResults = results.find((r:any) => r.questionRef === question?.ref);

  const answers = useMemo(() => {
    if (!quiz) return [];
    if (quiz.type === QuizTypes.RankingPoll) {
      return quiz.questionsSettings.answersOptions || [];
    } else {
      return question?.answers || [];
    }
  }, [quiz, question]);

  const questionAnswers = [...answers].sort((a, b) => {
    const aCount = questionResults?.answers[a.ref] || 0;
    const bCount = questionResults?.answers[b.ref] || 0;
    return bCount - aCount;
  });

  const allAnswers:number[] = [];

  Object.keys(questionResults?.answers || {}).forEach((key) => {
    if (answers.find(answer => answer.ref === key)) {
      allAnswers.push(questionResults.answers[key] || 0)
    }
  })

  const sum = allAnswers.reduce((a,b) => a+b, 0);

  const fetchResult = (isTimer: boolean) => {
    dispatch(getResultAnswers({ quizName: slug })).unwrap()
      .then(() => {
        if (!isTimer) {
          setDataLoaded(true);
        }
      })
      .catch(() => {
        if (!isTimer) {
          navigate('/404-not-found');
        }
      })
      .finally(() => setLoading(false));
  }

  // On compact (stadium/short) screens the question results and QR code can't stay
  // readable side by side, so on those screens the rotation also includes one extra
  // "slide" at the end - the QR code (with the logo) shown full-screen - before
  // looping back to the first question.
  const handleChangeQuestion = useCallback(() => {
    if (!questionPages) return;
    const totalSlides = isCompact ? questionPages.length + 1 : questionPages.length;
    setProgress(0);
    setQuestionIndex(prev => (prev + 1) % totalSlides);
  }, [questionPages, isCompact]);

  useEffect(() => {
    setLoading(true);
    fetchResult(false);

    interval = setInterval(() => { fetchResult(true) }, 10000);

    const rootDiv = document.getElementById('root');
    rootDiv?.classList.add('liveLeaderboardMode');

    return () => {
      clearInterval(interval);
      rootDiv?.classList.remove('liveLeaderboardMode');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quiz) return;
    if (![QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type)) navigate('/404-not-found');
    i18n.changeLanguage(quiz.language || 'en');
    document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
    // eslint-disable-next-line
  }, [quiz]);

  useEffect(() => {
    if (!dataLoaded) return;
    questionInterval = setInterval(() => { handleChangeQuestion() }, (quiz?.publicLeaderboard.questionsTimeout || 5) * 1000);

    return () => {
      clearInterval(questionInterval);
    }
    // eslint-disable-next-line
  }, [dataLoaded, isCompact]);

  useEffect(() => {
    if (!questionPages) return;
    const totalSlides = isCompact ? questionPages.length + 1 : questionPages.length;
    if (questionIndex >= totalSlides) setQuestionIndex(0);
  }, [isCompact, questionPages, questionIndex]);

  useEffect(() => {
    if (!dataLoaded) return;
    const step = 100 / ((quiz?.publicLeaderboard.questionsTimeout || 5) * 10);
    const progressInterval = setInterval(() => {
      setProgress(prev => (prev + step > 100 ? 100 : prev + step));
    }, 100);
  
    return () => clearInterval(progressInterval);
    // eslint-disable-next-line
  }, [dataLoaded, questionIndex]);

  const bgImageUrl = quiz ? quiz.publicLeaderboard.wallpaperImage : '';
  const portraitBgImageUrl = quiz ? quiz.publicLeaderboard.portraitWallpaperImage : '';
  
  if (loading || !result || !quiz) return <AppLoader />;
  return (
    <Box
      className={`
        h-full flex flex-col bg-cover bg-center
        ${isCompact ? 'overflow-hidden gap-2 px-4 py-2' : 'overflow-scroll p-4 landscape:justify-between portrait:gap-8 sm:p-8 xl:bg-right-top'}
      `}
      sx={{
        color: quiz?.publicLeaderboard.textColor,
        '@media screen and (orientation: portrait)': {
          backgroundImage: `url(${portraitBgImageUrl || bgImageUrl})`,
        },

        '@media screen and (orientation: landscape)': {
          backgroundImage: `url(${bgImageUrl || portraitBgImageUrl})`,
        }
      }}
    >
      <div className="shrink-0">
        {!isCompact && (
          <div className="flex justify-between items-center">
            <img
              src={quiz.logoImage} alt={quiz.quizName}
              className="object-contain shadow-primary rounded-lg h-12 sm:h-24"
              style={{ backgroundColor: quiz.logoImageBackgroundColor }}
            />
            {quiz.sponsorImage ? (
              <img
                src={quiz.sponsorImage} alt="sponsor"
                className="object-contain shadow-primary rounded-lg h-12 sm:h-24"
                style={{ backgroundColor: quiz.sponsorImageBackgroundColor }}
              />
            ) : null}
          </div>
        )}
        {!isCompact && (
          <div className="w-full landscape:w-2/3 text-center py-8">
            {quiz.publicLeaderboard.headerText ? (
              <h1 className="font-extrabold border-b border-white uppercase truncate py-8 text-2xl sm:text-8xl">{quiz.publicLeaderboard.headerText}</h1>
            ) : null}
            <h1 className="font-extrabold border-b border-white truncate py-8 text-2xl sm:text-7xl">{question?.title}</h1>
          </div>
        )}
        {isCompact && !isQrSlide && (
          <div className="flex items-center justify-between gap-8">
            <div className="flex-1 min-w-0 text-left">
              {quiz.publicLeaderboard.headerText ? (
                <h1 className="font-extrabold border-b border-white uppercase truncate py-0.5 text-3xl">{quiz.publicLeaderboard.headerText}</h1>
              ) : null}
              <h1 className="font-extrabold border-b border-white truncate py-0.5 text-3xl">{question?.title}</h1>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <img
                src={quiz.logoImage} alt={quiz.quizName}
                className="h-20 object-contain shadow-primary rounded-lg"
                style={{ backgroundColor: quiz.logoImageBackgroundColor }}
              />
              {quiz.sponsorImage ? (
                <img
                  src={quiz.sponsorImage} alt="sponsor"
                  className="h-20 object-contain shadow-primary rounded-lg"
                  style={{ backgroundColor: quiz.sponsorImageBackgroundColor }}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
      {isCompact && isQrSlide ? (
        <div className="flex-1 min-h-0 flex flex-col items-center gap-3 overflow-hidden">
          <div className={`shrink-0 flex items-center gap-3 w-full ${quiz.sponsorImage ? 'justify-between' : 'justify-center'}`}>
            <img
              src={quiz.logoImage} alt={quiz.quizName}
              className="h-16 object-contain shadow-primary rounded-lg"
              style={{ backgroundColor: quiz.logoImageBackgroundColor }}
            />
            {quiz.sponsorImage ? (
              <img
                src={quiz.sponsorImage} alt="sponsor"
                className="h-16 object-contain shadow-primary rounded-lg"
                style={{ backgroundColor: quiz.sponsorImageBackgroundColor }}
              />
            ) : null}
          </div>
          <QRCode isLargeScreen={isLargeScreen} isCompact={isCompact} />
        </div>
      ) : (
        <div className={isCompact ? 'flex-1 min-h-0 flex flex-row gap-2' : 'flex justify-between gap-8 portrait:flex-col'}>
          <div className={isCompact ? 'flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden' : 'w-full landscape:w-2/3'}>
            <AnswerList questionResults={questionResults} questionAnswers={questionAnswers} allAnswers={sum} />
            {questionPages && questionPages.length > 1 ? (
              <div className={`rounded-full shadow-primary-border shrink-0 ${isCompact ? 'mt-1 p-0.5' : 'mt-4 p-2'}`}>
                <div
                  className={isCompact ? 'h-2 rounded-full' : 'h-6 rounded-full'}
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(to bottom, rgb(232 238 143), rgb(106 210 107))',
                    transition: 'width 0.1s',
                  }}
                />
              </div>
            ) : null}
          </div>
          {!isCompact && <QRCode isLargeScreen={isLargeScreen} isCompact={isCompact} />}
        </div>
      )}
    </Box>
  );
}

export default LiveResultsPage;
